'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { signIn, useSession } from 'next-auth/react';
import { fetchRound, submitGuess, fetchRoundStats } from '../lib/api';
import { getScoreSegments } from '../lib/score';
import type { RoundData } from '../lib/types';
import { ZoomImage } from '../lib/zoomImage';
import { ShareButton } from './ShareButton';
import { VideoTeaser } from './VideoTeaser';
import { FeedbackPill } from './FeedbackPill';
import { TodayDistribution } from './TodayDistribution';
import { AllTimeLeaderboard } from './AllTimeLeaderboard';
import { HowToPlayCard } from './HowToPlayCard';
import { StreakCard } from './StreakCard';
import { KeepPlayingFooter } from '../../_components/KeepPlayingFooter';
import { DailySlateNav } from '../../_components/DailySlateNav';

interface AxisFeedback {
  matched: boolean;
  value?: string;
  attempted: boolean;
  points?: number;
}

export function GameBoard({
  date,
  initialRound,
  discordCta,
}: {
  date: string;
  initialRound?: RoundData;
  discordCta?: ReactNode;
}) {
  const { data: authSession, status: authStatus } = useSession();
  const userId = authSession?.userId;
  const userName = authSession?.screenname;
  const inputRef = useRef<HTMLInputElement>(null);
  const [round, setRound] = useState<RoundData | null>(initialRound ?? null);
  const [guess, setGuess] = useState('');
  const [level, setLevel] = useState(1);
  const [makeMatch, setMakeMatch] = useState<AxisFeedback>({
    matched: false,
    attempted: false,
  });
  const [modelMatch, setModelMatch] = useState<AxisFeedback>({
    matched: false,
    attempted: false,
  });
  const [genMatch, setGenMatch] = useState<AxisFeedback>({
    matched: false,
    attempted: false,
  });
  const [completed, setCompleted] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(!initialRound);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [answer, setAnswer] = useState<{
    make: string;
    model: string;
    generation: string;
    year_start?: number;
    year_end?: number;
  } | null>(null);
  const [funFact, setFunFact] = useState<string | null>(null);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [stats, setStats] = useState<{
    total_players: number;
    completed: number;
    avg_score: number | null;
  } | null>(null);
  const [streak, setStreak] = useState<number>(0);
  const [displayLevel, setDisplayLevel] = useState(1);
  const [transitionMs, setTransitionMs] = useState(600);

  useEffect(() => {
    if (!initialRound) return;
    // Read aspect from the tight crop — same source aspect, but we'd be
    // loading it for L1 anyway, so we don't pay an extra network round
    // trip just to size the canvas. Avoiding initialRound.image_url here
    // keeps the full photo out of the browser cache pre-completion.
    const img = new Image();
    img.onload = () => setAspectRatio(img.naturalWidth / img.naturalHeight);
    img.src = initialRound.tight_crop_url;
  }, [initialRound]);

  // Keep the visual zoom in sync with gameplay level until the round ends.
  useEffect(() => {
    if (completed) return;
    setTransitionMs(600);
    setDisplayLevel(level);
  }, [level, completed]);

  // Completion reveal: snap to tight zoom, then pull back to the full photo.
  useEffect(() => {
    if (!completed) return;
    setTransitionMs(0);
    setDisplayLevel(1);
    let raf2: number | undefined;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setTransitionMs(1500);
        setDisplayLevel(3);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 !== undefined) cancelAnimationFrame(raf2);
    };
  }, [completed]);

  useEffect(() => {
    if (authStatus === 'loading') return;
    // Anonymous + server-hydrated: no client fetch needed.
    if (initialRound && !userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    if (!initialRound) setLoading(true);
    fetchRound(date, userId)
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          if (!initialRound) {
            setError('Round not found');
            setLoading(false);
          }
          return;
        }
        setRound(data);
        if (data.streak !== undefined) setStreak(data.streak);
        if (data.session) {
          const s = data.session;
          setLevel(s.current_level || 1);
          setCompleted(s.completed);
          setScore(s.score);
          const a = data.answer;
          if (s.matched_make)
            setMakeMatch({
              matched: true,
              attempted: true,
              value: a?.make,
              points: s.make_points ?? undefined,
            });
          if (s.matched_model)
            setModelMatch({
              matched: true,
              attempted: true,
              value: a?.model,
              points: s.model_points ?? undefined,
            });
          if (s.matched_gen)
            setGenMatch({
              matched: true,
              attempted: true,
              value: a?.generation,
              points: s.gen_points ?? undefined,
            });
          if (s.completed && a) {
            setRevealed(true);
            setAnswer(a);
            setFunFact(data.fun_fact ?? null);
            fetchRoundStats(data.id)
              .then(setStats)
              .catch(() => {});
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (!initialRound) {
          setError(err.message || 'Failed to load round');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [date, userId, authStatus, initialRound]);

  const handleGuess = useCallback(async () => {
    if (!round || !guess.trim() || submitting || completed) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitGuess(
        round.id,
        guess.trim(),
        userId,
        userName,
      );
      if (result.error) {
        setError(result.error);
        setSubmitting(false);
        return;
      }
      if (result.make.matched)
        setMakeMatch({
          matched: true,
          value: result.make.value,
          attempted: true,
          points: result.make.points,
        });
      else if (!makeMatch.matched)
        setMakeMatch((prev) => ({ ...prev, attempted: true }));
      if (result.model.matched)
        setModelMatch({
          matched: true,
          value: result.model.value,
          attempted: true,
          points: result.model.points,
        });
      else if (!modelMatch.matched)
        setModelMatch((prev) => ({ ...prev, attempted: true }));
      if (result.gen.matched)
        setGenMatch({
          matched: true,
          value: result.gen.value,
          attempted: true,
          points: result.gen.points,
        });
      else if (!genMatch.matched)
        setGenMatch((prev) => ({ ...prev, attempted: true }));
      setLevel(result.current_level);
      setCompleted(result.completed);
      setScore(result.score);
      if (result.streak !== undefined) setStreak(result.streak);
      if (result.completed && result.answer) {
        setRevealed(true);
        setAnswer(result.answer);
        setFunFact(result.fun_fact ?? null);
      }
      if (result.completed && round) {
        fetchRoundStats(round.id)
          .then(setStats)
          .catch(() => {});
      }
      setGuess('');
    } catch {
      setError('Failed to submit guess');
    }
    setSubmitting(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [
    round,
    guess,
    submitting,
    completed,
    makeMatch.matched,
    modelMatch.matched,
    genMatch.matched,
    userId,
    userName,
  ]);

  if (loading) {
    return (
      <div
        style={{
          maxWidth: 600,
          margin: '0 auto',
          padding: '64px 0',
          textAlign: 'center',
          fontFamily: 'var(--font-sans)',
          fontWeight: 300,
          color: 'var(--fg-4)',
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  }

  if (error && !round) {
    return (
      <div
        style={{
          maxWidth: 600,
          margin: '0 auto',
          padding: '64px 0',
          textAlign: 'center',
          fontFamily: 'var(--font-sans)',
          color: '#b91c1c',
          fontSize: 14,
        }}
      >
        {error}
      </div>
    );
  }

  if (!round) return null;

  const remainingAxes: string[] = [];
  if (!makeMatch.matched) remainingAxes.push('make');
  if (!modelMatch.matched) remainingAxes.push('model');
  if (!genMatch.matched) remainingAxes.push('year or generation');
  const placeholder =
    level === 1
      ? 'e.g. 1990 Mazda MX-5 NA'
      : remainingAxes.length === 1
        ? `Just the ${remainingAxes[0]}`
        : remainingAxes.join(' + ');

  const remainingGuesses = Math.max(0, 3 - (level - 1));
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const [, m, d] = date.split('-');
  const dayNum = parseInt(d, 10);
  const monthName = months[parseInt(m, 10) - 1];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div className="mo-spot-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            className="mo-bg-card"
            style={{
              border: '1px solid var(--border-soft)',
              boxShadow: 'var(--card-stack)',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
            }}
          >
            {/* Header — eyebrow + apex chip + date stamp */}
            <div
              style={{
                padding: '18px 22px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontWeight: 600,
                      fontSize: 13,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'var(--fg-1)',
                    }}
                  >
                    Daily Carspotting · Name The Car
                  </span>
                  {!completed && (
                    <span
                      className="mo-bg-acc-soft"
                      style={{
                        fontFamily: 'var(--font-sans)',
                        fontSize: 11,
                        fontWeight: 500,
                        padding: '3px 10px',
                        borderRadius: 999,
                        color: 'var(--acc-deep)',
                      }}
                    >
                      {remainingGuesses}{' '}
                      {remainingGuesses === 1 ? 'guess' : 'guesses'} left
                    </span>
                  )}
                </div>
                <p
                  style={{
                    marginTop: 4,
                    fontFamily: 'var(--font-sans)',
                    fontSize: 12,
                    fontWeight: 300,
                    color: 'var(--fg-3)',
                  }}
                >
                  {completed
                    ? round.submitted_by_screenname
                      ? `Round complete · submitted by @${round.submitted_by_screenname}`
                      : 'Round complete'
                    : 'Miss → photo zooms out. Earlier guesses earn more points.'}
                </p>
              </div>
              <div
                aria-label={`${monthName} ${dayNum}`}
                className="mo-bg-card-soft"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: '1px solid var(--border-soft)',
                  minWidth: 54,
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 200,
                    fontSize: 22,
                    lineHeight: 1,
                    color: 'var(--fg-1)',
                  }}
                >
                  {dayNum}
                </span>
                <span
                  style={{
                    marginTop: 3,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 9.5,
                    fontWeight: 500,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--fg-3)',
                  }}
                >
                  {monthName}
                </span>
              </div>
            </div>

            {/* Image area — ZoomImage + corner ticks + zoom level pill / reveal banner */}
            <div
              className="mo-bg-denim-card"
              style={{
                position: 'relative',
                aspectRatio: aspectRatio ? `${aspectRatio}` : undefined,
                overflow: 'hidden',
              }}
            >
              <ZoomImage
                tightUrl={round.tight_crop_url}
                mediumUrl={round.medium_crop_url}
                fullUrl={round.image_url}
                level={displayLevel}
                centerX={round.center_x}
                centerY={round.center_y}
                tightRadius={round.tight_radius}
                mediumRadius={round.medium_radius}
                transitionMs={transitionMs}
              />
              <CornerTicks />
              {completed && revealed && answer && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    padding: '18px 22px 16px',
                    background:
                      'linear-gradient(180deg, transparent, rgba(7,28,75,0.82) 70%)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 16,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <span
                      className="mo-expanded"
                      style={{
                        fontSize: 11,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        fontWeight: 300,
                        color: 'var(--coral-deep)',
                      }}
                    >
                      The answer
                    </span>
                    <div
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontWeight: 200,
                        fontSize: 'clamp(20px, 2.4vw, 26px)',
                        letterSpacing: '-0.01em',
                        marginTop: 4,
                      }}
                    >
                      {answer.make} {answer.model}
                      <span style={{ opacity: 0.7 }}>
                        {' '}
                        · {answer.generation}
                      </span>
                    </div>
                    {answer.year_start && answer.year_end && (
                      <div
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          color: 'rgba(255,255,255,0.7)',
                          marginTop: 4,
                          letterSpacing: '0.04em',
                        }}
                      >
                        {answer.year_start === answer.year_end
                          ? answer.year_start
                          : `${answer.year_start}–${answer.year_end}`}
                      </div>
                    )}
                  </div>
                  {round.wikipedia_url && (
                    <a
                      href={round.wikipedia_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'var(--coral-deep)',
                        fontSize: 12,
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Wikipedia ↗
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Feedback row — per-axis pills */}
            <div
              className="mo-bg-card-soft"
              style={{
                margin: '14px 22px 0',
                padding: '10px 14px',
                border: '1px solid var(--border-soft)',
                borderRadius: 10,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 8,
              }}
            >
              <FeedbackPill label="Make" axis="make" {...makeMatch} />
              <FeedbackPill label="Model" axis="model" {...modelMatch} />
              <FeedbackPill label="Gen · Year" axis="gen" {...genMatch} />
            </div>

            {error && (
              <p
                style={{
                  margin: '10px 22px 0',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 12,
                  color: '#b91c1c',
                  textAlign: 'center',
                }}
              >
                {error}
              </p>
            )}

            {/* Input or completed footer */}
            {!completed ? (
              <div style={{ padding: '14px 22px 20px' }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <input
                    ref={inputRef}
                    autoFocus
                    type="search"
                    name="spot-guess"
                    id="spot-guess"
                    value={guess}
                    onChange={(e) => setGuess(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleGuess()}
                    placeholder={placeholder}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-1p-ignore
                    data-lpignore="true"
                    data-form-type="other"
                    disabled={submitting}
                    className="[&::-webkit-search-cancel-button]:appearance-none"
                    style={{
                      flex: '1 1 200px',
                      minWidth: 0,
                      height: 44,
                      padding: '0 14px',
                      borderRadius: 10,
                      border: '1px solid var(--border)',
                      background: 'var(--card-bg)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 14,
                      color: 'var(--fg-1)',
                      outline: 'none',
                      boxShadow: 'var(--card-stack)',
                    }}
                  />
                  <button
                    onClick={handleGuess}
                    disabled={submitting || !guess.trim()}
                    className="mo-bg-denim"
                    style={{
                      height: 44,
                      padding: '0 22px',
                      color: '#fff',
                      border: 0,
                      borderRadius: 10,
                      fontFamily: 'var(--font-sans)',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor:
                        submitting || !guess.trim() ? 'not-allowed' : 'pointer',
                      opacity: submitting || !guess.trim() ? 0.4 : 1,
                      boxShadow: 'var(--shadow-sm)',
                      transition:
                        'background .15s, box-shadow .15s, opacity .15s',
                    }}
                  >
                    {submitting ? '…' : 'Guess'}
                  </button>
                </div>
                <p
                  style={{
                    marginTop: 10,
                    fontFamily: 'var(--font-sans)',
                    fontSize: 11.5,
                    color: 'var(--fg-3)',
                    fontWeight: 300,
                  }}
                >
                  Partial credit works — any one of make / model / generation
                  scores on its own.
                </p>
              </div>
            ) : (
              <div
                style={{
                  padding: '16px 22px 22px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}
              >
                {funFact && (
                  <div
                    className="mo-bg-card-soft"
                    style={{
                      border: '1px solid var(--border-soft)',
                      borderRadius: 10,
                      padding: '14px 16px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontWeight: 600,
                        color: 'var(--coral-deep)',
                        marginBottom: 6,
                      }}
                    >
                      Fun fact
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontFamily: 'var(--font-sans)',
                        fontSize: 14,
                        lineHeight: 1.5,
                        fontStyle: 'italic',
                        color: 'var(--fg-1)',
                      }}
                    >
                      {funFact}
                    </p>
                  </div>
                )}
                {/* Score box on denim-ink */}
                <div
                  className="mo-bg-denim-card"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto minmax(0, 1fr)',
                    gap: 18,
                    padding: '18px 20px',
                    color: '#fff',
                    borderRadius: 10,
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: 'rgba(255,255,255,0.6)',
                        fontWeight: 600,
                      }}
                    >
                      Your score
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <span
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontWeight: 200,
                          fontSize: 52,
                          lineHeight: 1,
                          letterSpacing: '-0.015em',
                          display: 'inline-block',
                          color: '#fff',
                        }}
                      >
                        {score ?? 0}
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontWeight: 200,
                          fontSize: 52,
                          lineHeight: 1,
                          letterSpacing: '-0.015em',
                          color: 'rgba(255,255,255,0.32)',
                        }}
                      >
                        /9
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      minWidth: 0,
                    }}
                  >
                    <AxisScoreSquares
                      score={score ?? 0}
                      makePoints={
                        makeMatch.matched ? (makeMatch.points ?? 0) : 0
                      }
                      modelPoints={
                        modelMatch.matched ? (modelMatch.points ?? 0) : 0
                      }
                      genPoints={genMatch.matched ? (genMatch.points ?? 0) : 0}
                    />
                    {streak >= 2 && (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '3px 10px',
                          background: 'rgba(242, 168, 140, 0.14)',
                          border: '1px solid rgba(242, 168, 140, 0.32)',
                          borderRadius: 999,
                          fontFamily: 'var(--font-mono)',
                          fontSize: 10.5,
                          letterSpacing: '0.04em',
                          color: 'var(--coral)',
                        }}
                      >
                        <span aria-hidden>🔥</span>
                        {streak}-day streak
                      </div>
                    )}
                  </div>
                </div>

                {/* Submitted-by + Wikipedia (when not already in reveal banner) */}
                {revealed &&
                  answer &&
                  (round.submitted_by_screenname || round.wikipedia_url) && (
                    <p
                      style={{
                        fontFamily: 'var(--font-sans)',
                        fontSize: 12,
                        color: 'var(--fg-4)',
                        fontWeight: 300,
                        textAlign: 'center',
                      }}
                    >
                      {round.submitted_by_screenname && (
                        <>
                          Submitted by{' '}
                          <span style={{ color: 'var(--fg-3)' }}>
                            @{round.submitted_by_screenname}
                          </span>
                        </>
                      )}
                    </p>
                  )}

                {/* Sign-in nudge for anonymous players */}
                {!userId && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        signIn('discord', {
                          callbackUrl:
                            typeof window !== 'undefined'
                              ? window.location.pathname
                              : '/',
                        })
                      }
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 16px',
                        borderRadius: 999,
                        background: '#5865F2',
                        color: '#fff',
                        border: 0,
                        fontFamily: 'var(--font-sans)',
                        fontWeight: 500,
                        fontSize: 12,
                        cursor: 'pointer',
                        transition: 'background .15s',
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.09.09 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.09 16.09 0 0 0-4.8 0c-.14-.34-.35-.76-.54-1.09c-.01-.02-.04-.03-.07-.03c-1.5.26-2.93.71-4.27 1.33c-.01 0-.02.01-.03.02c-2.72 4.07-3.47 8.03-3.1 11.95c0 .02.01.04.03.05c1.8 1.32 3.53 2.12 5.24 2.65c.03.01.06 0 .07-.02c.4-.55.76-1.13 1.07-1.74c.02-.04 0-.08-.04-.09c-.57-.22-1.11-.48-1.64-.78c-.04-.02-.04-.08-.01-.11c.11-.08.22-.17.33-.25c.02-.02.05-.02.07-.01c3.44 1.57 7.15 1.57 10.55 0c.02-.01.05-.01.07.01c.11.09.22.17.33.26c.04.03.04.09-.01.11c-.52.31-1.07.56-1.64.78c-.04.01-.05.06-.04.09c.32.61.68 1.19 1.07 1.74c.03.01.06.02.09.01c1.72-.53 3.45-1.33 5.25-2.65c.02-.01.03-.03.03-.05c.44-4.53-.73-8.46-3.1-11.95c-.01-.01-.02-.02-.04-.02zM8.52 14.91c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.84 2.12-1.89 2.12zm6.97 0c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.83 2.12-1.89 2.12z" />
                      </svg>
                      Sign in with Discord to save your streak
                    </button>
                    <Link
                      href="/auth/signin"
                      style={{
                        fontFamily: 'var(--font-sans)',
                        fontSize: 11,
                        color: 'var(--fg-4)',
                        fontWeight: 300,
                        textDecoration: 'none',
                      }}
                    >
                      Or sign in with email
                    </Link>
                  </div>
                )}

                {/* Share */}
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <ShareButton
                    date={date}
                    score={score ?? 0}
                    makePoints={makeMatch.matched ? (makeMatch.points ?? 0) : 0}
                    modelPoints={
                      modelMatch.matched ? (modelMatch.points ?? 0) : 0
                    }
                    genPoints={genMatch.matched ? (genMatch.points ?? 0) : 0}
                    streak={streak}
                  />
                </div>

                {stats && stats.total_players > 0 && (
                  <p
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: 11.5,
                      color: 'var(--fg-3)',
                      fontWeight: 300,
                      textAlign: 'center',
                    }}
                  >
                    {stats.total_players}{' '}
                    {stats.total_players === 1 ? 'player has' : 'players have'}{' '}
                    attempted today
                    {stats.avg_score !== null &&
                      ` · avg score ${stats.avg_score}`}
                  </p>
                )}

                {discordCta}
              </div>
            )}
          </div>

          <DailySlateNav currentGame="spot" date={date} userId={userId} />

          {completed && (
            <KeepPlayingFooter
              game="spot"
              date={date}
              userId={userId}
              archiveHref="/games?game=spot"
              includeSelf
            />
          )}

          {userId && !completed && (
            <Link
              href="/spot/submit"
              style={{
                display: 'block',
                textAlign: 'center',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                color: 'var(--coral-deep)',
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              + Submit a round
            </Link>
          )}

          {completed && userId && (
            <Link
              href="/spot/submit"
              style={{
                display: 'block',
                textAlign: 'center',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                color: 'var(--coral-deep)',
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              Got a car to share? Submit a round →
            </Link>
          )}
        </div>

        {/* Sidebar: state-conditional companion cards. Collapses below the
            game card at <860px (see .mo-spot-grid in tokens.css). */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!completed ? (
            <>
              <HowToPlayCard />
              <StreakCard streak={streak} signedIn={!!userId} />
            </>
          ) : (
            <>
              <TodayDistribution
                roundId={round?.id}
                yourScore={score}
                useMock={
                  typeof window !== 'undefined' &&
                  new URLSearchParams(window.location.search).get('mock') ===
                    '1'
                }
              />
              <AllTimeLeaderboard
                currentUserId={userName ?? undefined}
                useMock={
                  typeof window !== 'undefined' &&
                  new URLSearchParams(window.location.search).get('mock') ===
                    '1'
                }
              />
              <VideoTeaser />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Static helper subcomponents ---------- */


const AXIS_TINT_LOCAL = {
  make: { main: 'var(--axis-make)', soft: 'var(--axis-make-soft)' },
  model: { main: 'var(--axis-model)', soft: 'var(--axis-model-soft)' },
  gen: { main: 'var(--axis-gen)', soft: 'var(--axis-gen-soft)' },
} as const;

function CornerTicks() {
  const tick = (css: CSSProperties) => (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        width: 14,
        height: 14,
        borderColor: 'rgba(255,255,255,0.5)',
        ...css,
      }}
    />
  );
  return (
    <>
      {tick({
        top: 10,
        left: 10,
        borderTop: '1px solid',
        borderLeft: '1px solid',
      })}
      {tick({
        top: 10,
        right: 10,
        borderTop: '1px solid',
        borderRight: '1px solid',
      })}
      {tick({
        bottom: 10,
        left: 10,
        borderBottom: '1px solid',
        borderLeft: '1px solid',
      })}
      {tick({
        bottom: 10,
        right: 10,
        borderBottom: '1px solid',
        borderRight: '1px solid',
      })}
    </>
  );
}

// Single left-packed 9-cell row mirroring the emoji share bar:
// make (+ unaccounted) → model → gen → missed. Driven by the same
// getScoreSegments helper as ShareButton so the on-screen viz and
// the share text always agree on tile count and color order.
function AxisScoreSquares({
  score,
  makePoints,
  modelPoints,
  genPoints,
}: {
  score: number;
  makePoints: number;
  modelPoints: number;
  genPoints: number;
}) {
  const seg = getScoreSegments(score, makePoints, modelPoints, genPoints);
  const cells: ('make' | 'model' | 'gen' | 'miss')[] = [
    ...Array(seg.make + seg.unaccounted).fill('make' as const),
    ...Array(seg.model).fill('model' as const),
    ...Array(seg.gen).fill('gen' as const),
    ...Array(seg.missed).fill('miss' as const),
  ];
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {cells.map((kind, i) => {
        const filled = kind !== 'miss';
        const fill =
          kind === 'miss'
            ? 'rgba(255,255,255,0.10)'
            : AXIS_TINT_LOCAL[kind].main;
        return (
          <span
            key={i}
            style={{
              width: 16,
              height: 16,
              borderRadius: 3,
              background: fill,
              border: filled
                ? '1px solid rgba(255,255,255,0.18)'
                : '1px solid rgba(255,255,255,0.16)',
              boxSizing: 'border-box',
            }}
          />
        );
      })}
    </span>
  );
}
